import * as dateFns from 'date-fns'
import React from 'react'

import { FinancialsTableContextProvider, useFinancialsTableContext } from '../../components/financials-table'
import { useFinancialsTablePersistScrollPosition } from '../../components/financials-table/useFinancialsTablePersistScrollPosition'
import { useCachedLoaderData } from '../../hooks/useCachedLoaderData'
import { EarningsWidget } from '../earnings-common/EarningsWidget'
import { EarningsWidgetCategory } from '../earnings-common/constants'
import { Earning, EarningsDataEnum, EarningsEstimateType, EarningsTimeframeEnum } from '../earnings-common/types'
import { getEffectiveTimeframe } from '../earnings-common/utils'
import { QuoteFinancialsTabMenu } from '../quote/components/QuoteFinancialsTabMenu'
import { QuoteStickySubMenu } from '../quote/components/QuoteStickySubMenu'
import EarningsHeader from './EarningsHeader'
import EarningsRevisions from './EarningsRevisions'
import EstimateBeats from './EstimateBeats'
import PriceReaction from './PriceReaction'
import WidgetSettingsSection from './WidgetSettingsSection'
import { DEFAULT_SETTINGS, FISCAL_YEAR_MARK, SETTINGS_COOKIE_KEY } from './constants'
import { EarningsData, EarningsRevisionsByType, EarningsSettings } from './types'
import { getIsSemiAnnualEarning, getYearAndPeriodInt } from './utils'

function EarningsComponent() {
  const { data: dataRaw } = useCachedLoaderData<EarningsData | null>()
  const { settings } = useFinancialsTableContext<EarningsSettings>()
  const isSemiAnnualEarning = getIsSemiAnnualEarning(dataRaw?.earningsData ?? [])
  const data = React.useMemo(() => {
    if (dataRaw === null) return null

    const sorter = (a: Earning, b: Earning) => {
      const { year: yearA, period: periodA } = getYearAndPeriodInt(a.fiscalPeriod, isSemiAnnualEarning)
      const { year: yearB, period: periodB } = getYearAndPeriodInt(b.fiscalPeriod, isSemiAnnualEarning)

      if ([yearA, yearB, periodA, periodB].includes(null)) {
        return 0
      }

      if (yearA === yearB) {
        return periodA! - periodB!
      }

      return yearA! - yearB!
    }

    const earningsData = [...(dataRaw?.earningsData ?? [])].sort(sorter)
    const earningsAnnualData = [...(dataRaw?.earningsAnnualData ?? [])].sort(sorter)

    const revisionsByType = (dataRaw?.earningsRevisionsData ?? []).reduce(
      (acc, revision) => {
        const fiscalKey = revision.fiscalPeriod.endsWith(FISCAL_YEAR_MARK) ? 'years' : 'quarters'
        const fiscalPeriods = acc[revision.estimateType][fiscalKey]
        let fiscalData = fiscalPeriods[revision.fiscalPeriod]
        if (!fiscalData) {
          fiscalData = []
          fiscalPeriods[revision.fiscalPeriod] = fiscalData
        }
        fiscalData.push({ ...revision, date: dateFns.parseISO(revision.estimateDate) })
        return acc
      },
      {
        [EarningsEstimateType.eps]: { quarters: {}, years: {} },
        [EarningsEstimateType.epsGaap]: { quarters: {}, years: {} },
        [EarningsEstimateType.sales]: { quarters: {}, years: {} },
      } as EarningsRevisionsByType
    )

    return {
      ...dataRaw,
      earningsData,
      earningsAnnualData,
      revisionData: !!dataRaw?.earningsRevisionsData?.length ? revisionsByType : null,
    }
  }, [dataRaw, isSemiAnnualEarning])

  useFinancialsTablePersistScrollPosition({
    categoryAreasCounts: [2, 2, 2],
    dataKeys: [EarningsDataEnum.yoyChange, EarningsDataEnum.yoyChangePct],
  })

  const hasQuarterlyData = !!data?.earningsData?.length
  const hasAnnualData = !!data?.earningsAnnualData?.length
  const hasData = !!data && (hasAnnualData || hasQuarterlyData)
  const timeframe = hasData
    ? getEffectiveTimeframe(data?.earningsData, data?.earningsAnnualData, settings.earningsTimeframe)
    : EarningsTimeframeEnum.quarterly

  return (
    <>
      <QuoteStickySubMenu
        leftContent={<QuoteFinancialsTabMenu />}
        rightContent={
          hasData ? (
            <WidgetSettingsSection
              isSemiAnnualEarning={isSemiAnnualEarning}
              showTimeframe={hasQuarterlyData && hasAnnualData}
              earningsTimeframe={timeframe}
            />
          ) : undefined
        }
      />

      {hasData ? (
        <div className="fv-container mb-4">
          <EarningsHeader
            earningsDate={data.earningsDate}
            earningsData={data.earningsData}
            earningsAnnualData={data.earningsAnnualData}
          />

          <div className="space-y-10">
            {[EarningsWidgetCategory.Eps, EarningsWidgetCategory.EpsGaap, EarningsWidgetCategory.Revenue].map(
              (earningsWidgetCategory) => (
                <EarningsWidget
                  key={earningsWidgetCategory}
                  data={data}
                  earningsWidgetCategory={earningsWidgetCategory}
                  priceReactionData={data?.priceReactionData}
                  settings={settings}
                />
              )
            )}

            {hasQuarterlyData && (
              <>
                {data?.priceReactionData.length > 0 && <PriceReaction priceReactionData={data?.priceReactionData} />}
                <EstimateBeats earningsData={data?.earningsData} />
              </>
            )}
            <EarningsRevisions revisionData={data?.revisionData} />
          </div>
        </div>
      ) : (
        <div className="fv-container flex h-60 items-center justify-center text-muted">
          Earnings are not available for this stock.
        </div>
      )}
    </>
  )
}

export default function Earnings() {
  return (
    <FinancialsTableContextProvider defaultSettings={DEFAULT_SETTINGS} cookieKey={SETTINGS_COOKIE_KEY}>
      <EarningsComponent />
    </FinancialsTableContextProvider>
  )
}
